import type {
    SiteBuilderBlock,
    SiteBuilderDesign,
    SiteBuilderRuntimePageLink,
    SiteNavigationItem,
    SiteNavigationMenu,
} from "./siteBuilderTypes";
import {
    SITE_NAVIGATION_VARIANTS as SITE_NAVIGATION_VARIANT_CATALOG,
    WEB_NAVIGATION_VARIANTS as WEB_NAVIGATION_VARIANT_CATALOG,
} from "./siteNavigationVariants.js";

export type {
    SiteNavigationItem,
    SiteNavigationMenu,
} from "./siteBuilderTypes";

export const SITE_NAVIGATION_BLOCK_TYPE = "navigation";
export const SITE_NAVIGATION_RUNTIME_ROLE = "navigation";
export const SITE_NAVIGATION_RUNTIME_ROLE_OPTION = "runtimeRole";
export const SITE_NAVIGATION_VARIANT_OPTION = "navigationVariant";
export const SITE_NAVIGATION_SOURCE_OPTION = "navigationSource";
export const SITE_NAVIGATION_THEME_OPTION = "navigationTheme";
export const SITE_NAVIGATION_ALIGNMENT_OPTION = "navigationAlignment";
export const SITE_NAVIGATION_OPEN_MODE_OPTION = "navigationOpenMode";
export const SITE_NAVIGATION_MENU_LOCATION_OPTION = "menuLocation";
export const SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION = "mobileMenuLocation";
export const SITE_NAVIGATION_MOBILE_BREAKPOINT_PX = 900;

export function isSiteNavigationMobileViewport(viewportWidth: number): boolean {
    return Number.isFinite(viewportWidth)
        && viewportWidth <= SITE_NAVIGATION_MOBILE_BREAKPOINT_PX;
}

export const SITE_NAVIGATION_VARIANTS = SITE_NAVIGATION_VARIANT_CATALOG;

export type SiteNavigationVariant = (typeof SITE_NAVIGATION_VARIANTS)[number];

export const WEB_NAVIGATION_VARIANTS = WEB_NAVIGATION_VARIANT_CATALOG;

export type WebNavigationVariant = (typeof WEB_NAVIGATION_VARIANTS)[number];

export type SiteNavigationAuthoredItemsOptions = Readonly<{
    excludeRuntimeSources?: boolean;
    slotKeys?: readonly string[];
}>;

const DEFAULT_AUTHORED_NAVIGATION_SLOT_KEYS = [
    "items",
    "children",
    "links",
    "content",
] as const;

function authoredNavigationText(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function authoredNavigationBoolean(value: unknown): boolean {
    return value === true || value === "true" || value === 1 || value === "1";
}

function authoredNavigationChildren(
    block: SiteBuilderBlock,
    slotKeys: readonly string[],
): SiteBuilderBlock[] {
    const slots = block.slots ?? {};

    for (const slotKey of slotKeys) {
        const children = slots[slotKey];

        if (Array.isArray(children) && children.length > 0) {
            return children;
        }
    }

    return block.children ?? [];
}

function authoredNavigationItemsFromBlocks(
    blocks: readonly SiteBuilderBlock[],
    path: string,
    options: Required<SiteNavigationAuthoredItemsOptions>,
): SiteNavigationItem[] {
    return blocks.flatMap((block, index) => {
        if (
            options.excludeRuntimeSources
            && authoredNavigationText(block.options?.runtimeSource).trim() !== ""
        ) {
            return [];
        }

        const itemPath = path === "" ? String(index) : `${path}.${index}`;
        const childItems = authoredNavigationItemsFromBlocks(
            authoredNavigationChildren(block, options.slotKeys),
            itemPath,
            options,
        );
        const label = authoredNavigationText(block.content?.label)
            || authoredNavigationText(block.content?.text)
            || authoredNavigationText(block.content?.title)
            || authoredNavigationText(block.content?.name)
            || authoredNavigationText(block.options?.label)
            || authoredNavigationText(block.options?.title);

        if (label.trim() === "") {
            return childItems;
        }

        const href = authoredNavigationText(block.content?.href)
            || authoredNavigationText(block.content?.url)
            || null;

        return [{
            children: childItems,
            href,
            label,
            openInNewTab: authoredNavigationBoolean(block.options?.openInNewTab)
                || authoredNavigationBoolean(block.options?.openInNewWindow)
                || authoredNavigationBoolean(block.content?.openInNewTab),
            type: authoredNavigationText(block.options?.navigationItemType)
                || authoredNavigationText(block.content?.type)
                || (block.type === "text" ? "custom" : block.type),
            uuid: block.uuid ?? `authored-navigation:${itemPath}`,
        }];
    });
}

/**
 * Adapts authored link/text trees to the neutral navigation contract. Empty
 * layout wrappers are flattened, while labelled nodes without href remain
 * grouping disclosures and keep their complete descendant hierarchy.
 */
export function siteNavigationAuthoredItems(
    navigationBlock: SiteBuilderBlock | null | undefined,
    options: SiteNavigationAuthoredItemsOptions = {},
): SiteNavigationItem[] {
    if (!navigationBlock) {
        return [];
    }

    const resolvedOptions: Required<SiteNavigationAuthoredItemsOptions> = {
        excludeRuntimeSources: options.excludeRuntimeSources ?? false,
        slotKeys: options.slotKeys ?? DEFAULT_AUTHORED_NAVIGATION_SLOT_KEYS,
    };

    return authoredNavigationItemsFromBlocks(
        authoredNavigationChildren(navigationBlock, resolvedOptions.slotKeys),
        "",
        resolvedOptions,
    );
}

export const SITE_NAVIGATION_SOURCES = [
    "auto",
    "authored",
    "sitePages",
    "siteMenu",
] as const;

export type SiteNavigationSource = (typeof SITE_NAVIGATION_SOURCES)[number];

export const SITE_NAVIGATION_THEMES = [
    "inherit",
    "surface",
    "inverse",
    "accent",
    "glass",
] as const;

export type SiteNavigationTheme = (typeof SITE_NAVIGATION_THEMES)[number];

export const SITE_NAVIGATION_ALIGNMENTS = [
    "start",
    "center",
    "end",
    "stretch",
] as const;

export type SiteNavigationAlignment = (typeof SITE_NAVIGATION_ALIGNMENTS)[number];

export const SITE_NAVIGATION_OPEN_MODES = [
    "hoverClick",
    "click",
] as const;

export type SiteNavigationOpenMode = (typeof SITE_NAVIGATION_OPEN_MODES)[number];
export type SiteNavigationViewport = "desktop" | "mobile";

export type SiteNavigationVariantFamily =
    | "audience"
    | "bordered"
    | "bold"
    | "cinematic"
    | "concierge"
    | "department"
    | "editorial"
    | "grocery"
    | "industrial"
    | "imageStrip"
    | "index"
    | "classic"
    | "monochrome"
    | "pills"
    | "planner"
    | "playful"
    | "reveal"
    | "routine"
    | "story"
    | "neon";

export type SiteNavigationVariantDefinition = Readonly<{
    family: SiteNavigationVariantFamily;
    featuredPanel: boolean;
    preferredColumns: number;
    supportsDeepTree: boolean;
    variant: SiteNavigationVariant;
}>;

/**
 * Presentation metadata used by the shared production renderer and its
 * previews. Labels deliberately stay in application i18n; this catalog owns
 * stable behavior only.
 */
export const SITE_NAVIGATION_VARIANT_DEFINITIONS: Readonly<
    Record<SiteNavigationVariant, SiteNavigationVariantDefinition>
> = {
    departmentMega: {
        family: "department",
        featuredPanel: true,
        preferredColumns: 4,
        supportsDeepTree: true,
        variant: "departmentMega",
    },
    audienceMega: {
        family: "audience",
        featuredPanel: true,
        preferredColumns: 4,
        supportsDeepTree: true,
        variant: "audienceMega",
    },
    editorialOverlay: {
        family: "editorial",
        featuredPanel: true,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "editorialOverlay",
    },
    roomPlanner: {
        family: "planner",
        featuredPanel: true,
        preferredColumns: 4,
        supportsDeepTree: true,
        variant: "roomPlanner",
    },
    routineMega: {
        family: "routine",
        featuredPanel: true,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "routineMega",
    },
    collectionConcierge: {
        family: "concierge",
        featuredPanel: true,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "collectionConcierge",
    },
    groceryHub: {
        family: "grocery",
        featuredPanel: false,
        preferredColumns: 5,
        supportsDeepTree: true,
        variant: "groceryHub",
    },
    productStory: {
        family: "story",
        featuredPanel: true,
        preferredColumns: 2,
        supportsDeepTree: true,
        variant: "productStory",
    },
    categoryPills: {
        family: "pills",
        featuredPanel: false,
        preferredColumns: 4,
        supportsDeepTree: true,
        variant: "categoryPills",
    },
    technicalIndex: {
        family: "index",
        featuredPanel: false,
        preferredColumns: 5,
        supportsDeepTree: true,
        variant: "technicalIndex",
    },
    lagunaClassic: {
        family: "classic",
        featuredPanel: false,
        preferredColumns: 4,
        supportsDeepTree: true,
        variant: "lagunaClassic",
    },
    borderedDropdown: {
        family: "bordered",
        featuredPanel: false,
        preferredColumns: 1,
        supportsDeepTree: true,
        variant: "borderedDropdown",
    },
    imageCategoryStrip: {
        family: "imageStrip",
        featuredPanel: true,
        preferredColumns: 4,
        supportsDeepTree: true,
        variant: "imageCategoryStrip",
    },
    monochromeDropdown: {
        family: "monochrome",
        featuredPanel: false,
        preferredColumns: 1,
        supportsDeepTree: true,
        variant: "monochromeDropdown",
    },
    industrialBar: {
        family: "industrial",
        featuredPanel: true,
        preferredColumns: 4,
        supportsDeepTree: true,
        variant: "industrialBar",
    },
    cinematicOverlay: {
        family: "cinematic",
        featuredPanel: true,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "cinematicOverlay",
    },
    minimalReveal: {
        family: "reveal",
        featuredPanel: false,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "minimalReveal",
    },
    boldUnderline: {
        family: "bold",
        featuredPanel: false,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "boldUnderline",
    },
    editorialRule: {
        family: "editorial",
        featuredPanel: false,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "editorialRule",
    },
    playfulFrame: {
        family: "playful",
        featuredPanel: false,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "playfulFrame",
    },
    neonLine: {
        family: "neon",
        featuredPanel: false,
        preferredColumns: 3,
        supportsDeepTree: true,
        variant: "neonLine",
    },
};

export type SiteNavigationSettings = Readonly<{
    alignment: SiteNavigationAlignment;
    menuLocation: string;
    mobileMenuLocation: string | null;
    openMode: SiteNavigationOpenMode;
    source: SiteNavigationSource;
    theme: SiteNavigationTheme;
    variant: SiteNavigationVariant;
}>;

export type SiteNavigationSettingsPatch = Partial<
    Omit<SiteNavigationSettings, "menuLocation" | "mobileMenuLocation">
> & {
    menuLocation?: string | null;
    mobileMenuLocation?: string | null;
};

export const DEFAULT_SITE_NAVIGATION_SETTINGS: SiteNavigationSettings = {
    alignment: "start",
    menuLocation: "header",
    mobileMenuLocation: null,
    openMode: "hoverClick",
    source: "auto",
    theme: "inherit",
    variant: "departmentMega",
};

export type SiteNavigationInspectorField = Readonly<{
    key: string;
    kind: "select" | "text";
    options?: readonly string[];
    scope: "options";
}>;

export const SITE_NAVIGATION_INSPECTOR_FIELDS: readonly SiteNavigationInspectorField[] = [
    {
        key: SITE_NAVIGATION_VARIANT_OPTION,
        kind: "select",
        options: SITE_NAVIGATION_VARIANTS,
        scope: "options",
    },
    {
        key: SITE_NAVIGATION_SOURCE_OPTION,
        kind: "select",
        options: SITE_NAVIGATION_SOURCES,
        scope: "options",
    },
    {
        key: SITE_NAVIGATION_THEME_OPTION,
        kind: "select",
        options: SITE_NAVIGATION_THEMES,
        scope: "options",
    },
    {
        key: SITE_NAVIGATION_ALIGNMENT_OPTION,
        kind: "select",
        options: SITE_NAVIGATION_ALIGNMENTS,
        scope: "options",
    },
    {
        key: SITE_NAVIGATION_OPEN_MODE_OPTION,
        kind: "select",
        options: SITE_NAVIGATION_OPEN_MODES,
        scope: "options",
    },
    {
        key: SITE_NAVIGATION_MENU_LOCATION_OPTION,
        kind: "text",
        scope: "options",
    },
    {
        key: SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION,
        kind: "text",
        scope: "options",
    },
];

const navigationVariantSet = new Set<string>(SITE_NAVIGATION_VARIANTS);
const navigationSourceSet = new Set<string>(SITE_NAVIGATION_SOURCES);
const navigationThemeSet = new Set<string>(SITE_NAVIGATION_THEMES);
const navigationAlignmentSet = new Set<string>(SITE_NAVIGATION_ALIGNMENTS);
const navigationOpenModeSet = new Set<string>(SITE_NAVIGATION_OPEN_MODES);
const legacyNavigationDocumentRoles = new Set([
    "navigation",
    "headerNavigation",
    "headerMenu",
    "pageNavigation",
    "footerNavigation",
    "footerPageNavigation",
]);

export function isSiteNavigationVariant(value: unknown): value is SiteNavigationVariant {
    return typeof value === "string" && navigationVariantSet.has(value);
}

export function normalizeSiteNavigationVariant(
    value: unknown,
    fallback: SiteNavigationVariant = DEFAULT_SITE_NAVIGATION_SETTINGS.variant,
): SiteNavigationVariant {
    return isSiteNavigationVariant(value) ? value : fallback;
}

export function isSiteNavigationSource(value: unknown): value is SiteNavigationSource {
    return typeof value === "string" && navigationSourceSet.has(value);
}

export function isSiteNavigationTheme(value: unknown): value is SiteNavigationTheme {
    return typeof value === "string" && navigationThemeSet.has(value);
}

export function isSiteNavigationAlignment(value: unknown): value is SiteNavigationAlignment {
    return typeof value === "string" && navigationAlignmentSet.has(value);
}

export function isSiteNavigationOpenMode(value: unknown): value is SiteNavigationOpenMode {
    return typeof value === "string" && navigationOpenModeSet.has(value);
}

function optionText(value: unknown): string | null {
    if (typeof value !== "string") {
        return null;
    }

    const normalized = value.trim();

    return normalized === "" ? null : normalized;
}

function normalizedLocation(value: unknown, fallback: string | null): string | null {
    return optionText(value)?.toLowerCase() ?? fallback;
}

function blockRuntimeRole(block: SiteBuilderBlock): string | null {
    return optionText(block.options?.[SITE_NAVIGATION_RUNTIME_ROLE_OPTION])
        ?? optionText(block.options?.siteRuntimeRole)
        ?? optionText(block.options?.semanticRole)
        ?? optionText(block.options?.role);
}

export function isSiteNavigationBlock(
    block: SiteBuilderBlock | null | undefined,
): boolean {
    if (!block) {
        return false;
    }

    if (block.type === SITE_NAVIGATION_BLOCK_TYPE || block.type === "headerMenu") {
        return true;
    }

    const documentRole = optionText(block.options?.documentRole);

    return blockRuntimeRole(block) === SITE_NAVIGATION_RUNTIME_ROLE
        || (documentRole !== null && legacyNavigationDocumentRoles.has(documentRole));
}

export function findSiteNavigationBlock(
    block: SiteBuilderBlock | null | undefined,
): SiteBuilderBlock | null {
    if (!block) {
        return null;
    }

    if (isSiteNavigationBlock(block)) {
        return block;
    }

    for (const children of Object.values(block.slots ?? {})) {
        for (const child of children) {
            const match = findSiteNavigationBlock(child);

            if (match) {
                return match;
            }
        }
    }

    for (const child of block.children ?? []) {
        const match = findSiteNavigationBlock(child);

        if (match) {
            return match;
        }
    }

    return null;
}

export function resolveSiteNavigationSettings(
    block: SiteBuilderBlock | null | undefined,
    defaults: Partial<SiteNavigationSettings> = {},
): SiteNavigationSettings {
    const fallback: SiteNavigationSettings = {
        ...DEFAULT_SITE_NAVIGATION_SETTINGS,
        ...defaults,
        menuLocation: normalizedLocation(
            defaults.menuLocation,
            DEFAULT_SITE_NAVIGATION_SETTINGS.menuLocation,
        ) ?? DEFAULT_SITE_NAVIGATION_SETTINGS.menuLocation,
        mobileMenuLocation: normalizedLocation(defaults.mobileMenuLocation, null),
    };
    const navigationBlock = findSiteNavigationBlock(block);
    const options = navigationBlock?.options ?? {};

    return {
        alignment: isSiteNavigationAlignment(options[SITE_NAVIGATION_ALIGNMENT_OPTION])
            ? options[SITE_NAVIGATION_ALIGNMENT_OPTION]
            : fallback.alignment,
        menuLocation: normalizedLocation(
            options[SITE_NAVIGATION_MENU_LOCATION_OPTION],
            fallback.menuLocation,
        ) ?? fallback.menuLocation,
        mobileMenuLocation: normalizedLocation(
            options[SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION],
            fallback.mobileMenuLocation,
        ),
        openMode: isSiteNavigationOpenMode(options[SITE_NAVIGATION_OPEN_MODE_OPTION])
            ? options[SITE_NAVIGATION_OPEN_MODE_OPTION]
            : fallback.openMode,
        source: isSiteNavigationSource(options[SITE_NAVIGATION_SOURCE_OPTION])
            ? options[SITE_NAVIGATION_SOURCE_OPTION]
            : fallback.source,
        theme: isSiteNavigationTheme(options[SITE_NAVIGATION_THEME_OPTION])
            ? options[SITE_NAVIGATION_THEME_OPTION]
            : fallback.theme,
        variant: normalizeSiteNavigationVariant(
            options[SITE_NAVIGATION_VARIANT_OPTION],
            fallback.variant,
        ),
    };
}

function settingsOptions(
    options: Record<string, unknown>,
    patch: SiteNavigationSettingsPatch,
): Record<string, unknown> {
    const next = { ...options };
    const set = (key: string, value: unknown): void => {
        if (value === null) {
            delete next[key];
            return;
        }

        if (value !== undefined) {
            next[key] = value;
        }
    };

    set(SITE_NAVIGATION_VARIANT_OPTION, patch.variant);
    set(SITE_NAVIGATION_SOURCE_OPTION, patch.source);
    set(SITE_NAVIGATION_THEME_OPTION, patch.theme);
    set(SITE_NAVIGATION_ALIGNMENT_OPTION, patch.alignment);
    set(SITE_NAVIGATION_OPEN_MODE_OPTION, patch.openMode);
    set(
        SITE_NAVIGATION_MENU_LOCATION_OPTION,
        patch.menuLocation === undefined
            ? undefined
            : normalizedLocation(patch.menuLocation, null),
    );
    set(
        SITE_NAVIGATION_MOBILE_MENU_LOCATION_OPTION,
        patch.mobileMenuLocation === undefined
            ? undefined
            : normalizedLocation(patch.mobileMenuLocation, null),
    );

    return next;
}

function withSettingsAtNavigationBlock(
    block: SiteBuilderBlock,
    patch: SiteNavigationSettingsPatch,
): SiteBuilderBlock {
    const nextOptions = settingsOptions(block.options ?? {}, patch);

    if (
        Object.keys(nextOptions).length === Object.keys(block.options ?? {}).length
        && Object.entries(nextOptions).every(([key, value]) => block.options?.[key] === value)
    ) {
        return block;
    }

    return { ...block, options: nextOptions };
}

/**
 * Updates only the first canonical or legacy navigation descendant. Unknown
 * options and the authored/runtime menu tree remain untouched.
 */
export function withSiteNavigationSettings(
    block: SiteBuilderBlock,
    patch: SiteNavigationSettingsPatch,
): SiteBuilderBlock {
    if (isSiteNavigationBlock(block)) {
        return withSettingsAtNavigationBlock(block, patch);
    }

    for (const [slotKey, slotBlocks] of Object.entries(block.slots ?? {})) {
        const childIndex = slotBlocks.findIndex((child) => findSiteNavigationBlock(child) !== null);

        if (childIndex < 0) {
            continue;
        }

        const child = slotBlocks[childIndex];
        const nextChild = withSiteNavigationSettings(child, patch);

        return nextChild === child
            ? block
            : {
                ...block,
                slots: {
                    ...(block.slots ?? {}),
                    [slotKey]: slotBlocks.map((candidate, index) => (
                        index === childIndex ? nextChild : candidate
                    )),
                },
            };
    }

    const childIndex = (block.children ?? []).findIndex(
        (child) => findSiteNavigationBlock(child) !== null,
    );

    if (childIndex < 0) {
        return block;
    }

    const child = block.children?.[childIndex];

    if (!child) {
        return block;
    }

    const nextChild = withSiteNavigationSettings(child, patch);

    return nextChild === child
        ? block
        : {
            ...block,
            children: block.children?.map((candidate, index) => (
                index === childIndex ? nextChild : candidate
            )),
        };
}

export type CreateSiteNavigationBlockOptions = Readonly<{
    children?: SiteBuilderBlock[];
    documentRole?: string;
    options?: Record<string, unknown>;
    settings?: SiteNavigationSettingsPatch;
    slots?: Record<string, SiteBuilderBlock[]>;
    uuid?: string;
}>;

export function createSiteNavigationBlock(
    input: CreateSiteNavigationBlockOptions = {},
): SiteBuilderBlock {
    const block: SiteBuilderBlock = {
        type: SITE_NAVIGATION_BLOCK_TYPE,
        ...(input.uuid ? { uuid: input.uuid } : {}),
        options: {
            [SITE_NAVIGATION_RUNTIME_ROLE_OPTION]: SITE_NAVIGATION_RUNTIME_ROLE,
            documentRole: input.documentRole ?? "navigation",
            ...(input.options ?? {}),
        },
        slots: input.slots ?? { content: [] },
        ...(input.children ? { children: input.children } : {}),
    };

    return withSettingsAtNavigationBlock(block, {
        ...DEFAULT_SITE_NAVIGATION_SETTINGS,
        ...(input.settings ?? {}),
    });
}

export function siteNavigationMenuLocationCandidates(
    settings: SiteNavigationSettings,
    viewport: SiteNavigationViewport,
    fallbackLocations: readonly string[] = viewport === "mobile"
        ? ["mobile", "header", "main"]
        : ["header", "main"],
): string[] {
    const configured = viewport === "mobile"
        ? settings.mobileMenuLocation ?? settings.menuLocation
        : settings.menuLocation;
    const candidates = [configured, ...fallbackLocations]
        .map((location) => normalizedLocation(location, null))
        .filter((location): location is string => location !== null);

    return candidates.filter((location, index) => candidates.indexOf(location) === index);
}

/** Empty configured menus are skipped so a populated fallback remains usable. */
export function resolveSiteNavigationMenu(
    menus: readonly SiteNavigationMenu[],
    settings: SiteNavigationSettings,
    viewport: SiteNavigationViewport = "desktop",
    languageCode?: string | null,
    fallbackLocations?: readonly string[],
): SiteNavigationMenu | null {
    const normalizedLanguage = languageCode?.trim().toLowerCase() ?? "";

    for (const location of siteNavigationMenuLocationCandidates(
        settings,
        viewport,
        fallbackLocations,
    )) {
        const matching = menus.filter((menu) => (
            menu.location.trim().toLowerCase() === location
            && menu.items.length > 0
        ));
        const localized = normalizedLanguage === ""
            ? null
            : matching.find((menu) => (
                menu.languageCode.trim().toLowerCase() === normalizedLanguage
            )) ?? null;
        const menu = localized ?? matching[0] ?? null;

        if (menu) {
            return menu;
        }
    }

    return null;
}

export function siteNavigationItemsFromPages(
    pages: readonly SiteBuilderRuntimePageLink[],
    limit = 8,
): SiteNavigationItem[] {
    const boundedLimit = Number.isFinite(limit)
        ? Math.max(0, Math.trunc(limit))
        : 8;

    return pages.slice(0, boundedLimit).map((page) => ({
        children: [],
        href: page.href ?? `/${page.slug}`,
        label: page.title,
        openInNewTab: false,
        type: "page",
        uuid: page.pageUuid ?? page.uuid ?? `page:${page.languageCode ?? ""}:${page.slug}`,
    }));
}

export type ResolveSiteNavigationItemsInput = Readonly<{
    authoredItems?: readonly SiteNavigationItem[];
    fallbackLocations?: readonly string[];
    languageCode?: string | null;
    menus?: readonly SiteNavigationMenu[];
    pages?: readonly SiteBuilderRuntimePageLink[];
    settings: SiteNavigationSettings;
    viewport?: SiteNavigationViewport;
}>;

/**
 * Resolves a runtime tree without persisting or copying a SiteMenu tree.
 * Explicit sources are deterministic; auto prefers the managed SiteMenu,
 * authored links and finally the current site's pages.
 */
export function resolveSiteNavigationItems({
    authoredItems = [],
    fallbackLocations,
    languageCode,
    menus = [],
    pages = [],
    settings,
    viewport = "desktop",
}: ResolveSiteNavigationItemsInput): readonly SiteNavigationItem[] {
    const menuItems = resolveSiteNavigationMenu(
        menus,
        settings,
        viewport,
        languageCode,
        fallbackLocations,
    )?.items ?? [];

    if (settings.source === "siteMenu") {
        return menuItems;
    }

    if (settings.source === "authored") {
        return authoredItems;
    }

    if (settings.source === "sitePages") {
        return siteNavigationItemsFromPages(pages);
    }

    if (menuItems.length > 0) {
        return menuItems;
    }

    return authoredItems.length > 0
        ? authoredItems
        : siteNavigationItemsFromPages(pages);
}

export type SiteNavigationThemeTokens = Readonly<{
    accent: string;
    accentText: string;
    background: string;
    border: string;
    muted: string;
    radius: string;
    shadow: string;
    surface: string;
    text: string;
}>;

function designColor(
    design: SiteBuilderDesign | null | undefined,
    key: keyof NonNullable<SiteBuilderDesign["colors"]>,
    fallback: string,
): string {
    const value = design?.colors?.[key];

    return typeof value === "string" && value.trim() !== ""
        ? value
        : fallback;
}

/**
 * Shared renderer tokens are derived from the active template design. A menu
 * variant controls composition, never a hard-coded palette.
 */
export function resolveSiteNavigationThemeTokens(
    design: SiteBuilderDesign | null | undefined,
    theme: SiteNavigationTheme = "inherit",
): SiteNavigationThemeTokens {
    const primary = designColor(design, "primary", "var(--site-primary)");
    const accent = designColor(design, "accent", "var(--site-accent)");
    const background = designColor(design, "background", "var(--site-background)");
    const surface = designColor(design, "surface", "var(--site-surface)");
    const text = designColor(design, "text", "var(--site-text)");
    const muted = designColor(design, "muted", "var(--site-muted)");
    const border = designColor(design, "border", "var(--site-border)");
    const radius = design?.layout?.radius ?? "var(--site-radius, 12px)";
    const base = {
        accent: primary,
        accentText: "var(--site-on-primary, #fff)",
        background: surface,
        border,
        muted,
        radius,
        shadow: "0 24px 70px color-mix(in srgb, var(--site-text) 16%, transparent)",
        surface: background,
        text,
    } satisfies SiteNavigationThemeTokens;

    if (theme === "inverse") {
        return {
            ...base,
            accent,
            accentText: "var(--site-on-accent, #fff)",
            background: "var(--site-inverse, var(--site-text))",
            border: "color-mix(in srgb, var(--site-on-inverse, #fff) 20%, transparent)",
            muted: "color-mix(in srgb, var(--site-on-inverse, #fff) 68%, transparent)",
            surface: "color-mix(in srgb, var(--site-inverse, var(--site-text)) 88%, var(--site-surface))",
            text: "var(--site-on-inverse, #fff)",
        };
    }

    if (theme === "accent") {
        return {
            ...base,
            accent: surface,
            accentText: text,
            background: primary,
            border: "color-mix(in srgb, var(--site-on-primary, #fff) 25%, transparent)",
            muted: "color-mix(in srgb, var(--site-on-primary, #fff) 72%, transparent)",
            surface: `color-mix(in srgb, ${primary} 88%, ${surface})`,
            text: "var(--site-on-primary, #fff)",
        };
    }

    if (theme === "glass") {
        return {
            ...base,
            background: `color-mix(in srgb, ${surface} 82%, transparent)`,
            border: `color-mix(in srgb, ${border} 72%, transparent)`,
            shadow: "0 26px 80px color-mix(in srgb, var(--site-text) 12%, transparent)",
            surface: `color-mix(in srgb, ${background} 78%, transparent)`,
        };
    }

    if (theme === "surface") {
        return {
            ...base,
            background,
            surface,
        };
    }

    return base;
}

export type SiteNavigationThemeStyle = Readonly<Record<
    `--site-navigation-${keyof SiteNavigationThemeTokens}`,
    string
>>;

export function siteNavigationThemeStyle(
    tokens: SiteNavigationThemeTokens,
): SiteNavigationThemeStyle {
    return {
        "--site-navigation-accent": tokens.accent,
        "--site-navigation-accentText": tokens.accentText,
        "--site-navigation-background": tokens.background,
        "--site-navigation-border": tokens.border,
        "--site-navigation-muted": tokens.muted,
        "--site-navigation-radius": tokens.radius,
        "--site-navigation-shadow": tokens.shadow,
        "--site-navigation-surface": tokens.surface,
        "--site-navigation-text": tokens.text,
    };
}
